/**
 * Bankruptcy / Game Over detection tick — pure helper extracted from
 * coreSlice.nextDay (02/06/2026 night refactor).
 *
 * Reads the post-tick balance sheet + the current insolvency streak +
 * the prior alert stage, and returns the new bankruptcy state
 * descriptors plus the messages to push.
 *
 * Pure : no `set()`, no `get()`. The orchestrator merges the descriptors
 * into its final set() and pushes messages via `pushCustomMessage`.
 */

import { computeBankruptcyStatus, buildGameOverSummary } from '../../utils/bankruptcyCheck';

/**
 * @param {Object} state - pre-flip game state
 * @param {Object} ctx
 * @param {Object} ctx.postTickSheet - balance sheet computed AFTER the
 *   tick applied treasury/vehicles/credits changes
 * @param {number} ctx.finalTreasury - final treasury for this tick
 * @param {number} ctx.newDay - the next-tick day (used to detect month rollover)
 * @returns {{
 *   nextStreak: number,
 *   alertStage: 'amber'|'rose'|'red'|null,
 *   alertLastPushDay: number,
 *   gameOverState: object|null,
 *   pendingShareholderMeeting: object|null,
 *   messages: Array<MessagePayload>,
 * }}
 */
export function runBankruptcyTick(state, ctx) {
  const { postTickSheet, finalTreasury, newDay } = ctx;
  const bk = computeBankruptcyStatus({
    treasury: finalTreasury,
    balanceSheet: postTickSheet,
    insolvencyStreak: state.insolvencyStreak || 0,
    isMonthlyClose: newDay === 1,
  });

  let gameOverState = state.gameOverState;
  let pendingShareholderMeeting = state.pendingShareholderMeeting;
  let alertStage = bk.alertStage;
  let alertLastPushDay = state.bankruptcyAlertLastPushDay || 0;
  const messages = [];

  // ── Liquidation triggered ──
  if (bk.liquidationTrigger && !gameOverState) {
    const summary = buildGameOverSummary(
      { ...state, totalDays: (state.totalDays || 0) + 1 },
      postTickSheet,
    );
    gameOverState = {
      stage: 1,
      trigger: bk.liquidationTrigger,
      liquidationDay: (state.totalDays || 0) + 1,
      summary,
    };
    pendingShareholderMeeting = null;
    alertStage = null;
    messages.push({
      senderType: 'urssaf',
      senderName: 'Tribunal de Commerce',
      tone: 'urgent',
      kind: 'warning',
      subject: 'Liquidation judiciaire prononcée',
      body:
        `Suite à ${
          bk.liquidationTrigger === 'technical'
            ? "votre situation d'insolvabilité technique (capitaux propres < -10 % de l'actif total)"
            : '30 jours consécutifs de cessation de paiements'
        }, le Tribunal de Commerce prononce la liquidation judiciaire de votre société. ` +
        'Un mandataire judiciaire prendra le contrôle des actifs restants.',
      ttlDays: 90,
    });
  } else if (bk.alertStage && !gameOverState) {
    // ── Stage escalation alert (debounced) ──
    const stagePrev = state.bankruptcyAlertStage;
    const daysSinceLastPush = (state.totalDays + 1) - alertLastPushDay;
    const stageEscalated = stagePrev !== bk.alertStage;
    if (stageEscalated && daysSinceLastPush >= 5) {
      const stageMessages = {
        amber: {
          subject: 'Vigilance — vos finances dérapent',
          body: `Vos capitaux propres sont négatifs et votre trésorerie déficitaire. Plus que ~${bk.daysUntilLiquidation} jours avant cessation de paiements officielle. Mesures correctives recommandées : geler les embauches, négocier un crédit, vendre un véhicule sous-utilisé.`,
          tone: 'formal',
          kind: 'warning',
        },
        rose: {
          subject: 'Danger — Cessation de paiements imminente',
          body: `Votre situation financière est critique : ${bk.daysUntilLiquidation} jours avant liquidation judiciaire. Une Assemblée Générale Extraordinaire peut être convoquée pour un apport personnel (50 % du déficit). À utiliser une seule fois par partie.`,
          tone: 'urgent',
          kind: 'warning',
        },
        red: {
          subject: 'LIQUIDATION IMMINENTE — dernière chance',
          body: `${bk.daysUntilLiquidation} jours avant le Tribunal de Commerce. Convoquez immédiatement une AGE depuis la bannière en haut d'écran si ce n'est pas déjà fait.`,
          tone: 'urgent',
          kind: 'warning',
        },
      };
      const m = stageMessages[bk.alertStage];
      if (m) {
        messages.push({
          senderType: 'bank',
          senderName: 'Conseiller bancaire',
          tone: m.tone,
          kind: m.kind,
          subject: m.subject,
          body: m.body,
          ttlDays: 30,
        });
        alertLastPushDay = (state.totalDays || 0) + 1;
      }
    }
    // Auto-open the AGE modal at first 'red' transition if not used yet.
    if (bk.alertStage === 'red' && stageEscalated && !state.emergencyShareholderUsed && !pendingShareholderMeeting) {
      pendingShareholderMeeting = {
        recapAmount: bk.recapAmount,
        deficit: bk.deficit,
        daysUntilLiquidation: bk.daysUntilLiquidation,
      };
    }
  }

  return {
    nextStreak: bk.nextStreak,
    alertStage,
    alertLastPushDay,
    gameOverState,
    pendingShareholderMeeting,
    messages,
  };
}
